<script setup lang="ts">
//#region imports
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useLs } from '@/setup/vueLs';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import type { UiTableNewField } from '@/types/table';
import type { GetLiveLatestEventsNewDepositsData, GetLiveLatestEventsNewDepositsResponse, GetLiveLatestEventsNewSportBetsData, GetLiveLatestEventsNewSportBetsResponse } from '@/types/openapi-ts';
//#endregion

//#region types
type SportBetRow = GetLiveLatestEventsNewSportBetsResponse['payload'][number];
type DepositRow = GetLiveLatestEventsNewDepositsResponse['payload'][number];

interface PlayerLastUrlData {
  bets_currency_code?: string;
  deposits_currency_code?: string;
}
//#endregion

//#region const
const LS_KEY = 'PlayerLast';
const refreshInterval = 30000;

const betsFields: UiTableNewField<SportBetRow>[] = [
  {
    name: 'events',
    align: 'left',
    headerAlign: 'left',
    format: 'event',
  },
  {
    name: 'events_count',
    align: 'left',
    headerAlign: 'left',
    width: '100',
    format: 'eventType',
  },
  {
    name: 'player_country_name',
    align: 'left',
    headerAlign: 'left',
    width: '100',
  },
  {
    name: 'bet_sum',
    align: 'right',
    headerAlign: 'left',
    width: '100',
    class: 'bold',
    format: 'formatMoney',
  },
  {
    name: 'coefficient',
    align: 'right',
    headerAlign: 'left',
    width: '80',
    class: 'bold',
  },
  {
    name: 'timestamp',
    align: 'left',
    headerAlign: 'left',
    width: '120',
    class: 'bold',
    format: 'date-time',
  },
];

const depositsFields: UiTableNewField<DepositRow>[] = [
  {
    name: 'player_country_name',
    align: 'left',
    headerAlign: 'left',
  },
  {
    name: 'deposit_sum',
    align: 'right',
    headerAlign: 'left',
    width: '100',
    format: 'formatMoney',
  },
  {
    name: 'payment_system_name',
    align: 'left',
    headerAlign: 'left',
    width: '100',
    class: 'method logo',
  },
  {
    name: 'timestamp',
    align: 'left',
    headerAlign: 'left',
    width: '120',
    class: 'bold',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const route = useRoute();
const ls = useLs();
const {checkRequest, checkRoute} = useDetectPermissions();
const { checkCurrencyCode } = useFormatCurrency();
const updateUrl = useUpdateUrl();
//#endregion

//#region state
const betsData = ref<GetLiveLatestEventsNewSportBetsResponse['payload']>([]);
const depositsData = ref<GetLiveLatestEventsNewDepositsResponse['payload']>([]);
const betsCurrencyCode = ref('');
const depositsCurrencyCode = ref('');
const isBetsLoading = ref(false);
const isDepositsLoading = ref(false);
const disabledRefresh = ref(false);
const counterId = ref<ReturnType<typeof setInterval>>();
//#endregion

//#region computed
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
//#endregion

//#region methods
function query() {
  return {
    bets_currency_code: betsCurrencyCode.value,
    deposits_currency_code: depositsCurrencyCode.value,
    param_playerlist: route.query.param_playerlist,
  };
}

function completedUrl() {
  updateUrl.updateGetParams(query());
}

function changeBetsCurrency(value: string) {
  fetchBets(value);
}

function changeDepositsCurrency(value: string) {
  fetchDeposits(value);
}

function autoRefreshOn() {
  counterId.value = setInterval(() => {
    fetchBets();
    fetchDeposits();
  }, refreshInterval);
}

function autoRefreshOff() {
  if (counterId.value) {
    clearInterval(counterId.value);
    counterId.value = undefined;
  }
}

function handleRefresh() {
  fetchDeposits();
  fetchBets();
  disabledRefresh.value = true;
  setTimeout(() => {
    disabledRefresh.value = false;
  }, 8000);
}

function fetchBets(currencyCode?: string) {
  isBetsLoading.value = true;

  if (currencyCode) {
    betsCurrencyCode.value = currencyCode;
  }

  const query: GetLiveLatestEventsNewSportBetsData['query'] = {
    player_id: [String(route.params.id)],
    currency_code: betsCurrencyCode.value,
  };

  api
    .getNewSportBets(query)
    .then((response) => {
      betsData.value = response.data.payload;
      betsCurrencyCode.value = response.data.misc?.currency_code ?? betsCurrencyCode.value;
    })
    .finally(() => {
      isBetsLoading.value = false;
    });
}

function fetchDeposits(currencyCode?: string) {
  isDepositsLoading.value = true;

  if (currencyCode) {
    depositsCurrencyCode.value = currencyCode;
  }

  const query: GetLiveLatestEventsNewDepositsData['query'] = {
    player_id: [String(route.params.id)],
    currency_code: depositsCurrencyCode.value,
  };

  api
    .getNewDeposits(query)
    .then((response) => {
      depositsData.value = response.data.payload;
      depositsCurrencyCode.value = response.data.misc?.currency_code ?? depositsCurrencyCode.value;
    })
    .finally(() => {
      isDepositsLoading.value = false;
    });
}
//#endregion

//#region watch
watch(betsCurrencyCode, (val) => {
  ls.set(`${LS_KEY}/bets_currency_code`, val);
  completedUrl();
});

watch(depositsCurrencyCode, (val) => {
  ls.set(`${LS_KEY}/deposits_currency_code`, val);
  completedUrl();
});

watch(() => route.params.id, () => {
  fetchBets();
  fetchDeposits();
});
//#endregion

//#region hooks
onBeforeMount(() => {
  if (checkRequest('last_bets_and_deposits_view')) {
    const urlData = updateUrl.getParseParams(route.hash) as PlayerLastUrlData;

    betsCurrencyCode.value = checkCurrencyCode(urlData.bets_currency_code ?? ls.get(`${LS_KEY}/bets_currency_code`));
    depositsCurrencyCode.value = checkCurrencyCode(urlData.deposits_currency_code ?? ls.get(`${LS_KEY}/deposits_currency_code`));

    fetchBets();
    fetchDeposits();
    autoRefreshOn();
  }
  checkRoute('last_bets_and_deposits_view');
});

onBeforeUnmount(() => {
  autoRefreshOff();
});
//#endregion
</script>

<template>
  <div class="t:flex t:gap-24">
    <div class="t:flex-1">
      <UiToolbar>
        <template v-slot:title>
          {{ $t('live.tables.last20bets') }}
        </template>
        <template v-slot:right-slot>
          <UiCurrencyPicker
            :model-value="betsCurrencyCode"
            :currencies="customerCurrencies"
            @update:model-value="changeBetsCurrency"
          />
        </template>
      </UiToolbar>
      <UiTableNew
        v-loading="isBetsLoading"
        :lazy-loading-delay="2000"
        :fields="betsFields"
        :data="betsData"
        :currency="betsCurrencyCode"
        lazy-loading
        disable-sort
        is-static
        i18n-path="live.list"
        element-loading-custom-class="data-loader-spinner"
      />
    </div>
    <div class="t:flex-1">
      <UiToolbar>
        <template v-slot:title>
          {{ $t('live.tables.last20deposits') }}
        </template>
        <template v-slot:right-slot>
          <UiCurrencyPicker
            :model-value="depositsCurrencyCode"
            :currencies="customerCurrencies"
            @update:model-value="changeDepositsCurrency"
          />
          <UiButton
            :disabled="disabledRefresh"
            icon="sync-alt"
            @click="handleRefresh"
          >
            {{ $t('reports.other.refresh') }}
          </UiButton>
        </template>
      </UiToolbar>
      <UiTableNew
        v-loading="isDepositsLoading"
        :lazy-loading-delay="2000"
        :fields="depositsFields"
        :data="depositsData"
        :currency="depositsCurrencyCode"
        lazy-loading
        disable-sort
        is-static
        i18n-path="live.list"
        element-loading-custom-class="data-loader-spinner"
      />
    </div>
  </div>
</template>

